import { Component, effect, EventEmitter, Input, OnChanges, Output, Signal, SimpleChanges } from '@angular/core';
import { Mod, ModItemCategory, ModTierItemVm, TieredMod } from '../../../models/item-data-models';
import { BaseComponent } from '../../base.component';
import { ItemDataService } from '../../../services/item-data.service';
import { ModTierService } from '../../../services/tiering/mod-tier.service';
import { ModDomain } from '../../../models/enums';

interface SiblingModVm {
  id: number;
  name: string;
  text: string;
  lvl: number;

  defaultTier?: number;
  specificTier?: number;
}

@Component({
  selector: 'app-primary-mod-label',
  templateUrl: './primary-mod-label.component.html',
  styleUrls: ['../../../tier-list-tab/tier-tab-common.scss', '../../label-common.scss', '../mod-label.component.scss']
})
export class PrimaryModLabelComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) mod!: ModTierItemVm;
  @Input() domain?: ModDomain;
  @Input() hideTooltip?: boolean;
  @Input() displayText?: string;
  @Input() itemCategory?: ModItemCategory;
  @Input() possiblyIncorrectSlot?: boolean;
  @Input() noApplicableTag?: boolean;

  @Output() resetToOriginalTier = new EventEmitter<ModTierItemVm>();

  defaultTier?: number;
  specificTier?: number;
  specificTierSlot?: string;
  siblingMods: SiblingModVm[] = [];

  showTooltip = false;
  highlightedText?: string;

  modData: Signal<Mod[]>;

  ModDomain = ModDomain;

  constructor(
    private itemDataService: ItemDataService,
    private modTierService: ModTierService
  ) {
    super();
    this.modData = this.itemDataService.getModData();

    effect(() => {
      this.modData();
      this.initSiblingsAndTiers();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mod']) {
      this.initSiblingsAndTiers();
    }
  }

  private initSiblingsAndTiers() {
    if (!this.mod.primaryMod) return;
    if (this.modData().length === 0) return;

    const allModsOfType = this.modData().filter(m => (this.domain ? m.domain === this.domain : true) && m.type === this.mod.primaryMod!.type).sort((a, b) => a.lvl - b.lvl);
    const defaultTiers = this.modTierService.assignTiersToModOfTypeGivenItemCategory(allModsOfType);
    const specificTiers = this.modTierService.assignTiersToModOfTypeGivenItemCategory(allModsOfType, this.itemCategory);

    this.siblingMods = defaultTiers.map(t => {
      const siblingMod = {
        id: t.mod.id,
        name: t.mod.name,
        text: t.mod.text,
        lvl: t.mod.lvl,
        defaultTier: t.tier,
      } as SiblingModVm;

      const specificTier = specificTiers.find(st => st.mod.id === t.mod.id)?.tier;
      if (specificTier && specificTier !== t.tier) {
        siblingMod.specificTier = specificTier;
      }

      return siblingMod;
    });

    this.defaultTier = defaultTiers.find(t => t.mod.id === this.mod.primaryMod!.id)?.tier;
    const specificTier = specificTiers.find(t => t.mod.id === this.mod.primaryMod!.id)?.tier;
    if (specificTier && specificTier !== this.defaultTier) {
      this.specificTier = specificTier;
      this.specificTierSlot = this.itemCategory?.slots[0];
    }
  }

  onMouseEnter() {
    if (this.mod.primaryMod?.text) {
      this.highlightedText = this.highlightValues(this.mod.primaryMod.text);
    }

    this.showTooltip = true;
  }

  onMouseLeave() {
    this.showTooltip = false;
  }

  highlightValues(effect: string, alwaysHighlight = false) {
    if (!this.settings.highlightStats() && !alwaysHighlight) {
      return effect;
    }
    // Avoid double processing if already highlighted
    if (effect.includes('<span class="it-effect-value">')) {
      return effect;
    }
    return effect.replace(/([\+\-]\(\d+(\.\d+)?-\d+(\.\d+)?\)|\(\+?-?\d+(\.\d+)?-\d+(\.\d+)?\)|\+?-?\d+(\.\d+)?)/g, '<span class="it-effect-value">$&</span>');
  }
}
